import type { Coords, LocationSummary } from './consts';

export interface GroupedLocationSummary extends Coords {
  key: string;
  name: string;
  aliases: LocationSummary[];
  rentalCount: number;
}

export const canonicalLocationName = (locations: LocationSummary[]): string => {
  const sorted = locations.slice().sort((a, b) => {
    const aName = a.name.trim();
    const bName = b.name.trim();
    const aHasQualifier = /[,/\u2013-]/.test(aName);
    const bHasQualifier = /[,/\u2013-]/.test(bName);

    if (b.rentalCount !== a.rentalCount) return b.rentalCount - a.rentalCount;
    if (aHasQualifier !== bHasQualifier) return aHasQualifier ? 1 : -1;
    if (aName.length !== bName.length) return aName.length - bName.length;
    return aName.localeCompare(bName);
  });

  return sorted[0]?.name || '';
};

export const groupLocationsByCoords = (
  locations: Iterable<LocationSummary> | null | undefined,
): GroupedLocationSummary[] => {
  const grouped = new Map<string, GroupedLocationSummary>();

  Array.from(locations || []).forEach(location => {
    if (location.rentalCount <= 0) return;

    const key = `${location.lat},${location.lng}`;
    const existing = grouped.get(key);
    if (existing) {
      existing.rentalCount += location.rentalCount;
      existing.aliases.push(location);
      existing.name = canonicalLocationName(existing.aliases);
      return;
    }

    grouped.set(key, {
      key,
      name: location.name,
      aliases: [location],
      lat: location.lat,
      lng: location.lng,
      rentalCount: location.rentalCount,
    });
  });

  return Array.from(grouped.values());
};
